import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { ReservationsService } from '@/api/services/reservations.service';
import type { IUpdateReservationSettingsDto } from '@/api/services/reservations.service';
import type {
  IReservationSettings,
  IReservationSettingsConfiguration,
} from '@/interfaces/IReservationSettings';
import { arrivalPreviewSettings } from '@/domain/reservations/reservation-preview';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useReservationsStore } from '@/stores/reservations.store';

export function useReservationsSettings() {
  const restaurant = useRestaurantStore();
  const reservations = useReservationsStore();
  const configuration = shallowRef<IReservationSettingsConfiguration | null>(
    null
  );
  const draft = ref<IReservationSettings | null>(null);
  const baseline = shallowRef<IReservationSettings | null>(null);
  const status = ref<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const error = shallowRef<unknown>(null);
  const saving = ref(false);
  const dirty = computed(() => !isEqual(draft.value, baseline.value));
  /** The turno previews follow the draft table times: their unsaved note follows only those. */
  const tableTimesDirty = computed(
    () =>
      !!draft.value &&
      !!baseline.value &&
      !isEqual(
        arrivalPreviewSettings(draft.value),
        arrivalPreviewSettings(baseline.value)
      )
  );
  let sequence = 0;
  let scopeSequence = 0;
  let settingsRevision = 0;
  let disposed = false;
  const key = () =>
    `${restaurant.restaurant?.id}:${restaurant.selectedBranch?.id}`;

  function discard() {
    draft.value = cloneDeep(baseline.value);
  }

  async function load({ preserveDraft = false } = {}) {
    const rid = restaurant.restaurant?.id;
    const bid = restaurant.selectedBranch?.id;
    if (!rid || !bid) return null;
    const requestKey = key();
    const request = ++sequence;
    const revision = settingsRevision;
    if (!configuration.value) status.value = 'loading';
    error.value = null;
    try {
      const response = await ReservationsService.getSettings(rid, bid);
      if (disposed || sequence !== request || requestKey !== key()) return null;
      // A child CRUD reload may have read before a concurrent settings save.
      // Keep its fresh collections/capabilities without restoring old settings.
      const storedSettings = configuration.value?.settings;
      configuration.value =
        revision !== settingsRevision && storedSettings
          ? {
              ...response,
              settings: {
                ...storedSettings,
                whatsappAvailable: response.settings.whatsappAvailable,
                mpConnected: response.settings.mpConnected,
                depositsAvailable: response.settings.depositsAvailable,
              },
            }
          : response;
      if (revision === settingsRevision && (!preserveDraft || !dirty.value)) {
        const {
          whatsappAvailable,
          mpConnected,
          depositsAvailable,
          ...stored
        } = response.settings;
        baseline.value = cloneDeep(stored);
        draft.value = cloneDeep(stored);
      }
      status.value = 'ready';
      return response;
    } catch (failure) {
      if (disposed || sequence !== request || requestKey !== key()) return null;
      error.value = failure;
      status.value = 'error';
      throw failure;
    }
  }

  /**
   * Lands a PUT response: new baseline, the response merged into the loaded
   * configuration (capability flags kept) and the live views refreshed.
   * `next` is the draft to keep (edits that must survive), or null to adopt
   * the response.
   */
  function applySaved(
    saved: IReservationSettings,
    next: IReservationSettings | null
  ) {
    ++settingsRevision;
    baseline.value = cloneDeep(saved);
    draft.value = next ?? cloneDeep(saved);
    if (configuration.value)
      configuration.value = {
        ...configuration.value,
        settings: { ...configuration.value.settings, ...saved },
      };
    void reservations.refreshActive();
  }

  async function save() {
    if (!draft.value || saving.value) return null;
    const snapshot = cloneDeep(draft.value);
    const requestKey = key();
    const requestScope = scopeSequence;
    const {
      id,
      restaurantId,
      branchId,
      createdAt,
      updatedAt,
      ...dto
    } = snapshot;
    saving.value = true;
    error.value = null;
    try {
      const saved = await ReservationsService.saveSettings(
        restaurantId,
        branchId,
        dto
      );
      if (disposed || key() !== requestKey || requestScope !== scopeSequence)
        return null;
      // Edits made while saving stay as they are (and dirty).
      applySaved(saved, isEqual(draft.value, snapshot) ? null : draft.value);
      return saved;
    } catch (failure) {
      if (disposed || key() !== requestKey || requestScope !== scopeSequence)
        return null;
      error.value = failure;
      throw failure;
    } finally {
      if (!disposed && key() === requestKey && requestScope === scopeSequence)
        saving.value = false;
    }
  }

  /**
   * Partial PUT of only `patch` (the setup guide's single-tap writes). A
   * dirty draft keeps its other fields and takes the patch; a clean one
   * adopts the response.
   */
  async function savePatch(
    patch: IUpdateReservationSettingsDto
  ): Promise<IReservationSettings | null> {
    const rid = restaurant.restaurant?.id;
    const bid = restaurant.selectedBranch?.id;
    if (!rid || !bid) return null;
    const requestKey = key();
    const requestScope = scopeSequence;
    error.value = null;
    try {
      const saved = await ReservationsService.saveSettings(rid, bid, patch);
      if (disposed || key() !== requestKey || requestScope !== scopeSequence)
        return null;
      // The stamp follows the baseline so the draft is dirty only by content.
      applySaved(
        saved,
        dirty.value && draft.value
          ? { ...draft.value, ...cloneDeep(patch), updatedAt: saved.updatedAt }
          : null
      );
      return saved;
    } catch (failure) {
      if (disposed || key() !== requestKey || requestScope !== scopeSequence)
        return null;
      error.value = failure;
      throw failure;
    }
  }

  watch(
    key,
    () => {
      ++sequence;
      ++scopeSequence;
      configuration.value = null;
      draft.value = null;
      baseline.value = null;
      saving.value = false;
      status.value = 'idle';
      void load().catch(() => undefined);
    },
    { immediate: true }
  );
  onScopeDispose(() => {
    disposed = true;
    ++sequence;
  });
  return {
    configuration,
    draft,
    status,
    error,
    saving,
    dirty,
    tableTimesDirty,
    load,
    save,
    savePatch,
    discard,
  };
}
